/**
 * Auth URL Sanitizer Utility
 * 
 * Extracts sensitive authentication tokens (`access_token`, `token`, `id_token`)
 * from incoming URL query parameters or hash fragments immediately upon application bootstrap.
 * 
 * Why:
 * 1. Security (RFC 6749 / CWE-598): Prevents tokens from leaking into browser history,
 *    HTTP Referer headers, browser caches, proxy logs, or when users copy/share links.
 * 2. Clean Routing: Ensures React Router and UI state do not propagate tokens across
 *    subsequent navigations or bookmarking.
 */

export function sanitizeAndExtractUrlToken(): string | null {
  if (typeof window === 'undefined') return null;

  let extractedToken: string | null = null;
  let searchChanged = false;
  let hashChanged = false;

  try {
    let cleanSearch = window.location.search || '';
    let cleanHash = window.location.hash || '';

    // 1. Check Search Query Parameters (?access_token=... or ?token=...)
    if (cleanSearch && (cleanSearch.includes('access_token=') || cleanSearch.includes('token=') || cleanSearch.includes('id_token='))) {
      const searchParams = new URLSearchParams(cleanSearch);
      if (searchParams.has('access_token')) {
        extractedToken = searchParams.get('access_token');
        searchParams.delete('access_token');
        searchChanged = true;
      }
      if (searchParams.has('token')) {
        if (!extractedToken) extractedToken = searchParams.get('token');
        searchParams.delete('token');
        searchChanged = true;
      }
      if (searchParams.has('id_token')) {
        searchParams.delete('id_token');
        searchChanged = true;
      }

      if (searchChanged) {
        const remainingQuery = searchParams.toString();
        cleanSearch = remainingQuery ? `?${remainingQuery}` : '';
      }
    }

    // 2. Check Hash Fragments (#access_token=... or #/path?access_token=...)
    if (cleanHash && (cleanHash.includes('access_token=') || cleanHash.includes('token=') || cleanHash.includes('id_token='))) {
      const queryIndex = cleanHash.indexOf('?');

      if (queryIndex !== -1) {
        const hashPath = cleanHash.slice(0, queryIndex);
        const hashQuery = cleanHash.slice(queryIndex + 1);
        const hashParams = new URLSearchParams(hashQuery);

        if (hashParams.has('access_token')) {
          if (!extractedToken) extractedToken = hashParams.get('access_token');
          hashParams.delete('access_token');
          hashChanged = true;
        }
        if (hashParams.has('token')) {
          if (!extractedToken) extractedToken = hashParams.get('token');
          hashParams.delete('token');
          hashChanged = true;
        }
        if (hashParams.has('id_token')) {
          hashParams.delete('id_token');
          hashChanged = true;
        }

        const rem = hashParams.toString();
        cleanHash = hashPath + (rem ? `?${rem}` : '');
      } else {
        // Fragment format: #access_token=...&token_type=...
        const fragment = cleanHash.replace(/^#\/?/, '');
        const fragParams = new URLSearchParams(fragment);

        if (fragParams.has('access_token')) {
          if (!extractedToken) extractedToken = fragParams.get('access_token');
          fragParams.delete('access_token');
          hashChanged = true;
        }
        if (fragParams.has('token')) {
          if (!extractedToken) extractedToken = fragParams.get('token');
          fragParams.delete('token');
          hashChanged = true;
        }
        if (fragParams.has('id_token')) {
          fragParams.delete('id_token');
          hashChanged = true;
        }

        const rem = fragParams.toString();
        cleanHash = rem ? `#${rem}` : '';
      }
    }

    // 3. Update browser history immediately if URL contained tokens
    if (searchChanged || hashChanged) {
      const cleanUrl = window.location.pathname + cleanSearch + cleanHash;
      window.history.replaceState(window.history.state || {}, document.title, cleanUrl);
    }

    // 4. Persist extracted token to localStorage for authenticated session hydration
    if (extractedToken) {
      window.localStorage.setItem('wix_auth_token', extractedToken);
    }
  } catch (err) {
    console.error("Auth URL sanitization warning:", err);
  }

  return extractedToken;
}

/**
 * Helper to strip access_token, token, and id_token from any query string
 * before React Router renders or executes `<Navigate />`
 */
export function cleanSearchParams(searchString: string): string {
  if (!searchString) return '';
  try {
    const params = new URLSearchParams(searchString);
    let changed = false;
    ['access_token', 'token', 'id_token'].forEach(key => {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    });
    if (!changed) return searchString.startsWith('?') ? searchString : `?${searchString}`;
    const result = params.toString();
    return result ? `?${result}` : '';
  } catch {
    return '';
  }
}
